import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { verifyAdmin, saveBulkFields, getCmsToken, setCmsToken, clearCmsToken } from '@/services/cms';

interface PendingChange {
    page: string;
    section: string;
    field: string;
    value: string;
    field_type: string;
    originalValue: string;
}

interface HistoryEntry {
    changes: Map<string, PendingChange>;
}

interface CmsContextValue {
    isAdmin: boolean;
    isEditMode: boolean;
    toggleEditMode: () => void;
    // Change tracking
    pendingChanges: Map<string, PendingChange>;
    trackChange: (page: string, section: string, field: string, value: string, originalValue: string, fieldType?: string) => void;
    discardChanges: () => void;
    changeCount: number;
    // Save / publish
    saveAll: () => Promise<boolean>;
    saving: boolean;
    lastError: string | null;
    // Undo / redo
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    // Auth
    login: (secret: string) => Promise<boolean>;
    logout: () => void;
    // Get the current value for a field (pending override or original)
    getPendingValue: (page: string, section: string, field: string) => string | undefined;
    // Bumps after each publish so hooks know to refetch
    publishVersion: number;
}

const CmsContext = createContext<CmsContextValue>({
    isAdmin: false,
    isEditMode: false,
    toggleEditMode: () => {},
    pendingChanges: new Map(),
    trackChange: () => {},
    discardChanges: () => {},
    changeCount: 0,
    saveAll: async () => false,
    saving: false,
    lastError: null,
    undo: () => {},
    redo: () => {},
    canUndo: false,
    canRedo: false,
    login: async () => false,
    logout: () => {},
    getPendingValue: () => undefined,
    publishVersion: 0,
});

function changeKey(page: string, section: string, field: string) {
    return `${page}::${section}::${field}`;
}

export function CmsProvider({ children }: { children: React.ReactNode }) {
    const [isAdmin, setIsAdmin] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());
    const [publishVersion, setPublishVersion] = useState(0);
    const [lastError, setLastError] = useState<string | null>(null);

    // Undo/redo stacks
    const undoStack = useRef<HistoryEntry[]>([]);
    const redoStack = useRef<HistoryEntry[]>([]);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    // Don't auto-verify via fetch on mount — the admin path is behind CF Access
    // which blocks fetch without the cookie. Trust the stored token; if invalid,
    // the next write will fail and the user gets prompted to re-enter.
    useEffect(() => {
        const token = getCmsToken();
        if (token) setIsAdmin(true);
    }, []);

    const pushUndo = useCallback((current: Map<string, PendingChange>) => {
        undoStack.current.push({ changes: new Map(current) });
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
    }, []);

    const trackChange = useCallback((page: string, section: string, field: string, value: string, originalValue: string, fieldType = 'text') => {
        setPendingChanges(prev => {
            pushUndo(prev);
            const next = new Map(prev);
            const key = changeKey(page, section, field);
            // If value matches original, remove the pending change
            if (value === originalValue) {
                next.delete(key);
            } else {
                next.set(key, { page, section, field, value, field_type: fieldType, originalValue });
            }
            return next;
        });
    }, [pushUndo]);

    const discardChanges = useCallback(() => {
        pushUndo(pendingChanges);
        setPendingChanges(new Map());
    }, [pendingChanges, pushUndo]);

    const undo = useCallback(() => {
        if (undoStack.current.length === 0) return;
        const prev = undoStack.current.pop()!;
        redoStack.current.push({ changes: new Map(pendingChanges) });
        setPendingChanges(prev.changes);
        setCanUndo(undoStack.current.length > 0);
        setCanRedo(true);
    }, [pendingChanges]);

    const redo = useCallback(() => {
        if (redoStack.current.length === 0) return;
        const next = redoStack.current.pop()!;
        undoStack.current.push({ changes: new Map(pendingChanges) });
        setPendingChanges(next.changes);
        setCanRedo(redoStack.current.length > 0);
        setCanUndo(true);
    }, [pendingChanges]);

    const saveAll = useCallback(async (): Promise<boolean> => {
        if (pendingChanges.size === 0) return true;
        setSaving(true);
        setLastError(null);
        try {
            const fields = Array.from(pendingChanges.values()).map(c => ({
                page: c.page,
                section: c.section,
                field: c.field,
                value: c.value,
                field_type: c.field_type,
            }));
            await saveBulkFields(fields);
            setPendingChanges(new Map());
            undoStack.current = [];
            redoStack.current = [];
            setCanUndo(false);
            setCanRedo(false);
            setPublishVersion(v => v + 1);
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to publish. Please try again.';
            setLastError(msg);
            console.error('Publish failed:', err);
            return false;
        } finally {
            setSaving(false);
        }
    }, [pendingChanges]);

    const toggleEditMode = useCallback(() => {
        setIsEditMode(prev => !prev);
    }, []);

    const login = useCallback(async (secret: string) => {
        setCmsToken(secret);
        try {
            const res = await verifyAdmin();
            if (res.is_admin) { setIsAdmin(true); return true; }
        } catch (err) {
            // If fetch fails (CORS, network, etc.), check if it's because CF Access
            // cookie is missing vs wrong secret. If we got redirected, the cookie is missing.
            console.warn('CMS verify failed:', err);
        }
        clearCmsToken();
        setIsAdmin(false);
        return false;
    }, []);

    const logout = useCallback(() => {
        clearCmsToken();
        setIsAdmin(false);
        setIsEditMode(false);
        setPendingChanges(new Map());
    }, []);

    const getPendingValue = useCallback((page: string, section: string, field: string) => {
        const key = changeKey(page, section, field);
        return pendingChanges.get(key)?.value;
    }, [pendingChanges]);

    return (
        <CmsContext.Provider value={{
            isAdmin, isEditMode, toggleEditMode,
            pendingChanges, trackChange, discardChanges,
            changeCount: pendingChanges.size,
            saveAll, saving,
            undo, redo, canUndo, canRedo,
            login, logout, getPendingValue, publishVersion, lastError,
        }}>
            {children}
        </CmsContext.Provider>
    );
}

export function useCms() {
    return useContext(CmsContext);
}
